import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { marginBy } from '@texas/utils/helpers/numberHelpers';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { evaluateProfit, profitTextColor } from '../sales/shared';
import { SelectedOfferTierPrice } from './OfferedOfferSelectionContext';

interface Props {
  isOpen: boolean;
  isDark: boolean;
  tierPrices: SelectedOfferTierPrice[];
  mode: 'fixed' | 'percentage';
  setMode: Dispatch<SetStateAction<'fixed' | 'percentage'>>;
  bulkPrice: string;
  setBulkPrice: Dispatch<SetStateAction<string>>;
  loading: boolean;
  onClose: () => void;
  onApply: () => Promise<void>;
  getPrimaryInquiryPrice: (tierPrice: SelectedOfferTierPrice) => number | null;
  calculateBulkPrice: (tierPrice: SelectedOfferTierPrice) => number | null;
}

export function SetOfferPricesModal({
  isOpen,
  isDark,
  tierPrices,
  mode,
  setMode,
  bulkPrice,
  setBulkPrice,
  loading,
  onClose,
  onApply,
  getPrimaryInquiryPrice,
  calculateBulkPrice,
}: Props) {
  const { t } = useTranslation();
  const isApplyDisabled =
    tierPrices.length === 0 ||
    tierPrices.some((tierPrice) => {
      const newPrice = calculateBulkPrice(tierPrice);
      return newPrice == null || newPrice <= 0;
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('offer.setPrice')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={3} wrap="wrap">
            <TexasInputWrapper label={t('offer.how')}>
              <Select
                value={mode}
                onChange={(e) =>
                  setMode(e.target.value as 'fixed' | 'percentage')
                }
              >
                <option value="fixed">{t('offer.fixed')}</option>
                <option value="percentage">{t('offer.percentage')}</option>
              </Select>
            </TexasInputWrapper>
            <TexasInputWrapper
              label={
                mode === 'fixed' ? t('offer.price') : t('offer.percentage')
              }
            >
              <Input
                type="number"
                min={mode === 'fixed' ? 0.01 : undefined}
                step="any"
                placeholder={mode === 'fixed' ? '-' : '0'}
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
              />
            </TexasInputWrapper>
          </Flex>
          <TableContainer mt={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('offer.offer')}</Th>
                  <Th>{t('general.quantity')}</Th>
                  <Th>{t('inquiry.price')}</Th>
                  <Th>{t('offer.currentSalesPrice')}</Th>
                  <Th>{t('offer.newSalesPrice')}</Th>
                  <Th>{t('variant.sales.margin')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tierPrices.map((tierPrice) => {
                  const inquiryPrice = getPrimaryInquiryPrice(tierPrice);
                  const newPrice = calculateBulkPrice(tierPrice);
                  const margin =
                    newPrice != null && inquiryPrice != null
                      ? marginBy(newPrice, inquiryPrice, false)
                      : null;
                  const profit =
                    newPrice != null && inquiryPrice != null
                      ? evaluateProfit(newPrice, inquiryPrice)
                      : null;

                  return (
                    <Tr key={tierPrice.id}>
                      <Td>{tierPrice.rowLabel}</Td>
                      <Td>{tierPrice.quantity}</Td>
                      <Td>
                        {inquiryPrice != null
                          ? `${inquiryPrice} ${tierPrice.currencyCode}`
                          : '-'}
                      </Td>
                      <Td>
                        {tierPrice.price != null
                          ? `${tierPrice.price} ${tierPrice.currencyCode}`
                          : '-'}
                      </Td>
                      <Td>
                        {newPrice != null
                          ? `${newPrice} ${tierPrice.currencyCode}`
                          : '-'}
                      </Td>
                      <Td
                        color={
                          profit == null
                            ? 'gray.500'
                            : profitTextColor(profit, isDark)
                        }
                      >
                        {margin != null ? `${margin.toFixed(1)}%` : '-'}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="texas-solid"
            isLoading={loading}
            isDisabled={isApplyDisabled}
            onClick={onApply}
          >
            {t('general.apply')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
